import React, { useState } from 'react';

import { Box } from '@rbilabs/components-library';
import { useIntl } from 'react-intl';

import ActionButton, { ActionButtonVariants } from 'components/action-button';
import { IDeliveryAddress } from 'generated/rbi-graphql';
import useMediaQuery from 'hooks/use-media-query';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';

import DeliveryTerms from '../delivery-terms';
import { StyledModalContent, StyledModalHeader } from '../styled';

import DeliveryAddressItem from './delivery-address-item';
import { AddressList } from './styled';
import { IDeliveryAddressesProps } from './types';

const DeliveryAddresses: React.FC<IDeliveryAddressesProps> = ({
  addresses,
  onCreate,
  setAddress,
  isLoading = false,
}) => {
  const { formatMessage } = useIntl();
  const [selected, setSelected] = useState(-1);
  const { logEvent } = useMParticleContext();
  const isDesktop = useMediaQuery('desktop');

  const onSelectAddress = async () => {
    const address = addresses[selected];
    logEvent(CustomEventNames.SELECT_DELIVERY_SAVED_ADDRESS, EventTypes.Other);

    const selectedAddressComponents = {
      addressLine1: address.addressLine1,
      addressLine2: address.addressLine2 || undefined,
      city: address.city,
      state: address.state || '',
      streetNumber: address.streetNumber || '',
      route: address.route || '',
      zip: address.zip || '',
      country: address.country || '',
    };

    let addressCoordinates;
    if (address.latitude && address.longitude) {
      addressCoordinates = {
        lat: address.latitude,
        lng: address.longitude,
      };
    }

    const selectedPhoneNumber = address.phoneNumber;
    setAddress({
      address: selectedAddressComponents,
      phoneNumber: selectedPhoneNumber,
      coordinates: addressCoordinates,
    });
  };

  return (
    <>
      <StyledModalContent data-testid="delivery-addresses">
        <StyledModalHeader>{formatMessage({ id: 'deliverySelectYourAddress' })}</StyledModalHeader>
      </StyledModalContent>
      <AddressList>
        {addresses?.map((address: IDeliveryAddress, index: number) => (
          <DeliveryAddressItem
            key={index}
            address={address}
            selected={index === selected}
            onSelect={() => setSelected(index)}
          />
        ))}
      </AddressList>
      <>
        <DeliveryTerms />
        <Box flexDirection="column" center padding="0 1rem">
          <Box width={isDesktop ? '50%' : '100%'} center margin="0 0 1rem">
            <ActionButton
              data-testid="select-delivery-address"
              disabled={isLoading || selected < 0}
              isLoading={isLoading}
              onClick={onSelectAddress}
              fullWidth
              variant={ActionButtonVariants.PRIMARY}
            >
              {formatMessage({ id: 'deliverHere' })}
            </ActionButton>
          </Box>
          <Box width={isDesktop ? '50%' : '100%'} center margin="0 0 1rem">
            <ActionButton
              data-testid="create-delivery-address"
              disabled={isLoading}
              isLoading={isLoading}
              onClick={() => onCreate()}
              fullWidth
              variant={ActionButtonVariants.OUTLINE}
            >
              {formatMessage({ id: 'deliveryAddNewAddress' })}
            </ActionButton>
          </Box>
        </Box>
      </>
    </>
  );
};

export default DeliveryAddresses;
